import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { NFTData, NFTMetadata, NFTGroup } from '../types';
import { CONTRACT_ABI, getContractConfig, getContractAddresses } from '../config/contract';

export const useNFTs = () => {
  const [groups, setGroups] = useState<NFTGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [completed, setCompleted] = useState<number>(0);
  const didInit = useRef<boolean>(false);

  const fetchNFTs = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = getContractConfig();
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const addresses = getContractAddresses();

      setGroups([]);
      setCompleted(0);

      // Determine total across addresses
      const supplies = await Promise.all(addresses.map(async (addr) => {
        try {
          const c = new ethers.Contract(addr, CONTRACT_ABI, provider);
          const s = await c.currentSupply();
          return { addr, supply: Number(s) };
        } catch {
          return { addr, supply: 0 };
        }
      }));
      const grandTotal = supplies.reduce((acc, s) => acc + s.supply, 0);
      setTotal(grandTotal);

      // Fetch each group sequentially (addresses already newest->oldest)
      for (const { addr, supply } of supplies) {
        // Init group
        setGroups((prev) => [...prev, { address: addr, nfts: [] }]);

        if (supply === 0) continue;

        const contract = new ethers.Contract(addr, CONTRACT_ABI, provider);
        const tokenIds = Array.from({ length: supply }, (_, i) => i);
        const CONCURRENCY = 8;
        let index = 0;

        const runNext = async (): Promise<void> => {
          const i = index++;
          if (i >= tokenIds.length) return;
          const tokenId = tokenIds[i];
          try {
            const nft = await fetchSingleNFT(contract, tokenId, addr);
            setCompleted((c) => c + 1);
            setGroups((prev) => prev.map((g) => {
              if (g.address !== addr) return g;
              if (g.nfts.some((n) => n.tokenId === nft.tokenId)) return g;
              const nn = [...g.nfts, nft];
              // Descending by token id (newest first)
              nn.sort((a, b) => b.tokenId - a.tokenId);
              return { ...g, nfts: nn };
            }));
          } finally {
            await runNext();
          }
        };

        const workers = Array.from({ length: Math.min(CONCURRENCY, tokenIds.length) }, () => runNext());
        await Promise.all(workers);
      }
    } catch (err) {
      console.error('Error fetching NFTs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch NFTs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleNFT = async (contract: ethers.Contract, tokenId: number, address?: string): Promise<NFTData> => {
    try {
      // Get token URI and parse metadata
      const tokenURI = await contract.tokenURI(tokenId);
      let metadata: NFTMetadata;

      try {
        if (typeof tokenURI === 'string' && tokenURI.startsWith('"') && tokenURI.endsWith('"') && tokenURI.includes('ipfs://')) {
          // Handle IPFS URLs
          const ipfsUrl = tokenURI.slice(1, -1);
          const gatewayUrl = ipfsUrl.replace('ipfs://', 'https://gateway.pinata.cloud/ipfs/');
          const response = await fetch(gatewayUrl);
          if (response.ok) {
            metadata = await response.json();
          } else {
            throw new Error('Failed to fetch IPFS metadata');
          }
        } else {
          // Handle direct JSON metadata
          metadata = JSON.parse(tokenURI);
        }
      } catch (parseError) {
        console.warn(`Failed to parse metadata for token ${tokenId}:`, parseError);
        // Create fallback metadata
        metadata = {
          name: `Octopus #${tokenId}`,
          description: 'A unique octopus NFT with randomly generated traits',
          attributes: [
            { trait_type: 'Token ID', value: tokenId.toString() }
          ]
        };
      }

      // Get additional contract data
      const [owner, siteMetadata] = await Promise.allSettled([
        contract.ownerOf(tokenId),
        contract.getTokenSiteMetadata(tokenId)
      ]);

      const nftData: NFTData = {
        tokenId,
        metadata,
        owner: owner.status === 'fulfilled' ? owner.value : undefined,
        siteName: siteMetadata.status === 'fulfilled' ? siteMetadata.value[0] : undefined,
        siteURL: siteMetadata.status === 'fulfilled' ? siteMetadata.value[1] : undefined,
        contractAddress: address
      };

      return nftData;
    } catch (err) {
      console.error(`Error fetching NFT ${tokenId}:`, err);
      // Return minimal NFT data
      return {
        tokenId,
        metadata: {
          name: `Octopus #${tokenId}`,
          description: 'NFT data unavailable',
          attributes: [
            { trait_type: 'Token ID', value: tokenId.toString() },
            { trait_type: 'Status', value: 'Unavailable' }
          ]
        }
      };
    }
  };

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    fetchNFTs();
  }, []);

  return {
    groups,
    loading,
    error,
    refetch: fetchNFTs,
    total,
    completed
  };
};
